/**
 * SharePoint rows in, domain types out.
 *
 * This is duller than it looks and worth doing carefully, because every mistake
 * here is silent. A tolerance that arrives as the string `"5"` and is stored
 * without coercion makes `415 * "5" / 100` produce a band by accident; a
 * `RequiresPhoto` that arrives as `null` and is treated as truthy adds a photo
 * slot to every check on the board; a `Nominal` of `0` dropped by a falsy test
 * turns a real limit into no limit at all.
 *
 * The rules:
 *  - a lookup column reads back as `<Name>Id`, never as the name,
 *  - `null` means absent, and under `exactOptionalPropertyTypes` an absent
 *    optional must be *omitted*, not set to `undefined`,
 *  - numbers may arrive as strings,
 *  - `0` and `false` are values, not absences.
 */

import type {
  Asset, BreakerType, ResponseType, ShotGroup, ShotProfile,
  TemplateItem, TemplateSection, ToleranceMode,
} from '../domain/types.js';

/** A row as SharePoint hands it over. */
export type SpRow = Record<string, unknown>;

export function num(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function text(v: unknown): string | undefined {
  if (v === null || v === undefined) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
}

/** SharePoint booleans arrive as true/false, 0/1 or "Yes"/"No" depending on the path. */
export function bool(v: unknown): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') return ['true', '1', 'yes'].includes(v.trim().toLowerCase());
  return false;
}

/** Spreads a value into an object only when it is present, so optionals stay absent. */
const opt = <K extends string, V>(key: K, value: V | undefined): Partial<Record<K, V>> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, V>);

const RESPONSE_TYPES: ReadonlySet<string> = new Set<ResponseType>([
  'pass_fail_na', 'measured_pass_fail', 'yes_no_applicable',
  'action_reaction_result', 'per_circuit', 'meter_verification', 'point_verification',
]);
const TOLERANCE_MODES: ReadonlySet<string> = new Set<ToleranceMode>([
  'percent', 'absolute', 'max', 'min', 'none',
]);
const BREAKER_TYPES: ReadonlySet<string> = new Set<BreakerType>([
  'ACB', 'MCCB', 'Busbar',
  'VCB', 'SF6-CB', 'LBS', 'Fuse-Switch', 'VT-Panel', 'Metering',
  'Starter', 'Tap-off',
  'N/A',
]);
const SHOT_GROUPS: ReadonlySet<string> = new Set<ShotGroup>([
  'Phase Rotation & Earth Loop', 'Meter Verification', 'CB Status',
]);

export class MappingError extends Error {}

function require<T>(value: T | undefined, what: string, row: SpRow): T {
  if (value === undefined) {
    throw new MappingError(`${what} missing on row ${String(row.Id ?? row.ID ?? '?')}`);
  }
  return value;
}

/**
 * An unknown choice value is refused rather than defaulted.
 *
 * Defaulting a `ResponseType` to `pass_fail_na` would render a measured check as
 * a pass/fail button, and the reading would never be captured at all. Better a
 * loud failure on one row than a silently wrong form on a board.
 */
function choice<T extends string>(v: unknown, allowed: ReadonlySet<string>, what: string, row: SpRow): T {
  const s = text(v);
  if (s === undefined || !allowed.has(s)) {
    throw new MappingError(`${what} ${JSON.stringify(v)} is not one of ${[...allowed].join(', ')} on row ${String(row.Id ?? '?')}`);
  }
  return s as T;
}

export function toSection(row: SpRow): TemplateSection {
  return {
    id: require(num(row.Id ?? row.ID), 'Id', row),
    name: require(text(row.Title), 'Title', row),
    seq: num(row.Seq) ?? 0,
    templateId: require(num(row.TemplateId), 'TemplateId', row),
    applicableByDefault: row.ApplicableByDefault === undefined ? true : bool(row.ApplicableByDefault),
    ...opt('description', text(row.SectionDescription)),
  };
}

export function toItem(row: SpRow): TemplateItem {
  return {
    id: require(num(row.Id ?? row.ID), 'Id', row),
    label: require(text(row.Title), 'Title', row),
    seq: num(row.Seq) ?? 0,
    sectionId: require(num(row.SectionId), 'SectionId', row),
    responseType: choice<ResponseType>(row.ResponseType, RESPONSE_TYPES, 'ResponseType', row),
    // A blank mode means "no numeric criteria", which the verdict engine already
    // handles — it is the one choice that may legitimately be empty.
    toleranceMode: text(row.ToleranceMode) === undefined
      ? 'none'
      : choice<ToleranceMode>(row.ToleranceMode, TOLERANCE_MODES, 'ToleranceMode', row),
    requiresPhoto: bool(row.RequiresPhoto),
    ...opt('description', text(row.ItemDescription)),
    ...opt('expectedReaction', text(row.ExpectedReaction)),
    ...opt('acceptanceCriteria', text(row.AcceptanceCriteria)),
    ...opt('unit', text(row.Unit)),
    ...opt('nominal', num(row.Nominal)),
    ...opt('tolerance', num(row.Tolerance)),
    ...opt('shotType', text(row.ShotType)),
  };
}

/** The limits as the project received them, alongside what they are now. */
export interface ItemLimits {
  itemId: number;
  current: Pick<TemplateItem, 'nominal' | 'tolerance' | 'toleranceMode'>;
  default: Pick<TemplateItem, 'nominal' | 'tolerance' | 'toleranceMode'>;
  overridden: boolean;
  editedBy?: string;
  editedAt?: string;
  note?: string;
}

export function toLimits(row: SpRow): ItemLimits {
  const mode = (v: unknown): ToleranceMode =>
    text(v) === undefined ? 'none' : choice<ToleranceMode>(v, TOLERANCE_MODES, 'ToleranceMode', row);
  return {
    itemId: require(num(row.Id ?? row.ID), 'Id', row),
    current: { ...opt('nominal', num(row.Nominal)), ...opt('tolerance', num(row.Tolerance)), toleranceMode: mode(row.ToleranceMode) },
    default: {
      ...opt('nominal', num(row.DefaultNominal)),
      ...opt('tolerance', num(row.DefaultTolerance)),
      toleranceMode: mode(row.DefaultToleranceMode),
    },
    overridden: bool(row.LimitOverridden),
    ...opt('editedBy', text(row.LimitEditedBy)),
    ...opt('editedAt', text(row.LimitEditedAt)),
    ...opt('note', text(row.LimitNote)),
  };
}

export function toAsset(row: SpRow): Asset {
  return {
    id: require(num(row.Id ?? row.ID), 'Id', row),
    tag: require(text(row.Title), 'Title', row),
    projectId: require(num(row.ProjectId), 'ProjectId', row),
    equipmentType: text(row.EquipmentType) ?? 'Other',
    // A breaker with no type in the register is real — M3D2-AU7-QF1 is on the
    // one-line diagram with no settings row — so it maps to 'N/A' rather than
    // failing the whole load.
    breakerType: BREAKER_TYPES.has(text(row.BreakerType) ?? '') ? (text(row.BreakerType) as BreakerType) : 'N/A',
    ...opt('parentAssetTag', text(row.ParentAssetTag)),
    ...opt('manufacturer', text(row.Manufacturer)),
    ...opt('model', text(row.Model)),
    ...opt('serialNumber', text(row.SerialNumber)),
    ...opt('location', text(row.AssetLocation)),
    ...opt('qrPayload', text(row.QRPayload)),
    ...opt('requiredShotCount', num(row.RequiredShotCount)),
  };
}

export function toShotProfile(row: SpRow): ShotProfile {
  return {
    id: require(num(row.Id ?? row.ID), 'Id', row),
    shotCode: require(text(row.Title), 'Title', row),
    breakerType: choice<BreakerType>(row.BreakerType, BREAKER_TYPES, 'BreakerType', row),
    shotGroup: choice<ShotGroup>(row.ShotGroup, SHOT_GROUPS, 'ShotGroup', row),
    measurementGroup: text(row.MeasurementGroup) ?? '',
    viewpoint: text(row.Viewpoint) ?? '',
    seq: num(row.Seq) ?? 0,
    required: row.Required === undefined ? true : bool(row.Required),
    ...opt('description', text(row.ShotDescription)),
  };
}

export interface MapResult<T> {
  rows: T[];
  /** Rows that could not be mapped, kept with their reason rather than dropped in silence. */
  rejected: { row: SpRow; reason: string }[];
}

/**
 * Maps a page of rows, surviving bad ones.
 *
 * One malformed row must not blank a whole board's script — but it must not
 * vanish either, or the register quietly shrinks and nobody knows which check
 * went missing. So: map what maps, keep the rest with a reason for the sync
 * screen to show.
 */
export function mapAll<T>(rows: readonly SpRow[], map: (row: SpRow) => T): MapResult<T> {
  const out: MapResult<T> = { rows: [], rejected: [] };
  for (const row of rows) {
    try {
      out.rows.push(map(row));
    } catch (e) {
      out.rejected.push({ row, reason: e instanceof Error ? e.message : String(e) });
    }
  }
  return out;
}
